// Cache for variant data to improve performance
let variantDataCache = null;
let imagePreloadCache = new Map();

// Function to wait for advancedPreorderProductJson with improved performance
const waitForVariantData = (maxAttempts = 50, interval = 100) => {
    // Return cached data if available
    if (variantDataCache) {
        return Promise.resolve(variantDataCache);
    }

    return new Promise((resolve, reject) => {
        let attempts = 0;

        const check = () => {
            if (window.advancedPreorderProductJson?.variants) {
                variantDataCache = window.advancedPreorderProductJson;
                resolve(variantDataCache);
            } else if (attempts >= maxAttempts) {
                reject(new Error("Variant data not available after maximum attempts"));
            } else {
                attempts++;
                setTimeout(check, interval);
            }
        };

        check();
    });
};

// Enhanced function to check if product should skip image display
const shouldSkipImageDisplay = (productData) => {
    // Skip if has shipping accessories tag AND no meaningful variants
    const hasShippingAccessoriesTag =
        productData.tags &&
        Array.isArray(productData.tags) &&
        productData.tags.includes("_tab_shipping-accessories");

    // Check if product has meaningful variants (not just "Default Title")
    const hasMeaningfulVariants =
        productData.variants &&
        productData.variants.length > 1 ||
        (productData.variants &&
            productData.variants.some(
                (variant) =>
                    (variant.option1 && variant.option1 !== "Default Title") ||
                    (variant.option2 && variant.option2 !== "Default Title") ||
                    (variant.option3 && variant.option3 !== "Default Title")
            ));

    console.log("Image display check:", {
        tags: productData.tags,
        hasShippingAccessoriesTag,
        hasMeaningfulVariants,
        variants: productData.variants?.map((v) => ({
            option1: v.option1,
            option2: v.option2,
            option3: v.option3,
        })),
    });

    // Only skip if it has shipping accessories tag AND no meaningful variants
    const shouldSkip = hasShippingAccessoriesTag && !hasMeaningfulVariants;
    console.log("Should skip image display:", shouldSkip);

    return shouldSkip;
};

// Preload image function (unchanged from previous version)
const preloadImage = (src) => {
    if (imagePreloadCache.has(src)) {
        return imagePreloadCache.get(src);
    }

    const promise = new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(src);
        img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
        img.src = src;
    });

    imagePreloadCache.set(src, promise);
    return promise;
};

// Create and manage loading spinner (unchanged from previous version)
const createLoadingSpinner = () => {
    const spinner = document.createElement("div");
    spinner.className = "loading-spinner";
    spinner.innerHTML = `
          <style>
              .loading-spinner {
                  position: absolute;
                  top: 50%;
                  left: 50%;
                  transform: translate(-50%, -50%);
                  width: 40px;
                  height: 40px;
                  border: 4px solid #f3f3f3;
                  border-top: 4px solid #000000;
                  border-radius: 50%;
                  animation: spin 1s linear infinite;
                  z-index: 1000;
              }
              @keyframes spin {
                  0% { transform: translate(-50%, -50%) rotate(0deg); }
                  100% { transform: translate(-50%, -50%) rotate(360deg); }
              }
          </style>
      `;
    return spinner;
};

// Enhanced image update function with accessory support
const updateProductImage = async (
    variantId,
    targetElement,
    showSpinner = true
) => {
    console.log("=== updateProductImage called ===");
    console.log("variantId:", variantId);
    console.log("targetElement:", targetElement);

    try {
        const variantData = await waitForVariantData();
        console.log("variantData loaded:", variantData);

        // Check if image display should be skipped
        if (shouldSkipImageDisplay(variantData)) {
            console.log("Skipping image display due to shipping accessories tag");
            // Remove existing image if present
            const existingImg = targetElement.querySelector("img");
            if (existingImg) {
                existingImg.remove();
            }
            return; // Exit early - no image should be shown
        }

        // Show loading spinner only if we're going to show an image
        if (showSpinner) {
            const spinner = createLoadingSpinner();
            targetElement.style.position = "relative";
            targetElement.appendChild(spinner);
        }

        // Determine the image to display
        let imageToShow = null;

        // If a specific variant is selected, find its image
        if (variantId) {
            const variant = variantData.variants.find(
                (v) => v.id.toString() === variantId.toString()
            );
            console.log("updateProductImage - Selected variant:", variant);
            imageToShow = variant?.featured_image;
            console.log("updateProductImage - imageToShow from variant:", imageToShow);
        }

        // If no variant image found or no variant selected, use product's featured image
        if (!imageToShow) {
            console.log(
                "updateProductImage - Using product's featured image:",
                variantData.featured_image
            );
            imageToShow = variantData.featured_image;
        }

        console.log("updateProductImage - Final image to show:", imageToShow);

        if (imageToShow) {
            console.log("updateProductImage - Preloading image:", imageToShow.src);
            // Preload the image
            await preloadImage(imageToShow.src);
            console.log("updateProductImage - Image preloaded successfully");

            // Find or create the image element
            let imgElement = targetElement.querySelector("img");
            console.log("updateProductImage - Existing img element:", imgElement);

            if (!imgElement) {
                console.log("updateProductImage - Creating new img element");
                imgElement = document.createElement("img");
                imgElement.style.width = "100%";
                imgElement.style.height = "auto";
                imgElement.style.objectFit = "contain";
                imgElement.style.opacity = "0";
                imgElement.style.transition = "opacity 0.3s ease-in-out";
                targetElement.appendChild(imgElement);
                console.log("updateProductImage - New img element appended");
            }

            // Update image with fade effect
            console.log("updateProductImage - Setting src to:", imageToShow.src);
            imgElement.setAttribute("src", imageToShow.src);
            imgElement.setAttribute("alt", imageToShow.alt || "Product Image");

            // Fade in the image
            setTimeout(() => {
                imgElement.style.opacity = "1";
                console.log("updateProductImage - Image faded in");
            }, 50);
        } else {
            console.log("updateProductImage - No image found to display");
        }
    } catch (error) {
        console.error("updateProductImage - Error:", error);
    } finally {
        // Remove loading spinner
        const spinner = targetElement.querySelector(".loading-spinner");
        if (spinner) {
            spinner.remove();
        }
        console.log("=== updateProductImage completed ===");
    }
};

// Initialize the observer with improved performance and accessory support
const initializeObserver = async () => {
    try {
        const variantData = await waitForVariantData();

        console.log("Initializing observer with variant data:", {
            tags: variantData.tags,
            featured_image: variantData.featured_image,
            variants_count: variantData.variants?.length,
        });

        // Don't set default image if display should be skipped
        if (shouldSkipImageDisplay(variantData)) {
            console.log("Skipping initialization due to shipping accessories tag");
            return;
        }

        // Set default image if no variant is selected
        const productViewers = document.querySelectorAll(".product_viewer--iframe");
        console.log("Found product viewers:", productViewers.length);

        productViewers.forEach(async (viewer) => {
            if (!viewer.querySelector("img")) {
                // Use first variant's image if available, otherwise use product's featured image
                const defaultVariant = variantData.variants[0];
                console.log("Default variant:", defaultVariant);

                if (defaultVariant?.featured_image) {
                    console.log("Using default variant image");
                    await updateProductImage(defaultVariant.id, viewer, false);
                } else if (variantData.featured_image) {
                    console.log("Using product featured image");
                    await updateProductImage(null, viewer, false);
                } else {
                    console.log("No images available");
                }
            }
        });

        let productViewerObserver = new IntersectionObserver(
            (entries, observer) => {
                entries.forEach((entry) => {
                    const el = entry.target;
                    const variantId = new URLSearchParams(window.location.search).get(
                        "variant"
                    );

                    if (entry.intersectionRatio > 0.9 && variantId) {
                        updateProductImage(variantId, el);
                        productViewerObserver.unobserve(el);
                    }
                });
            },
            { threshold: 0.8 }
        );

        productViewers.forEach((product) => {
            productViewerObserver.observe(product);
        });
    } catch (error) {
        console.error("Error initializing observer:", error);
    }
};

// Debounce function (unchanged from previous version)
const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

// Handle URL changes with debouncing
let lastVariantId = new URLSearchParams(window.location.search).get("variant");
const handleUrlChange = debounce(() => {
    const currentVariantId = new URLSearchParams(window.location.search).get(
        "variant"
    );
    if (currentVariantId !== lastVariantId) {
        lastVariantId = currentVariantId;
        initializeObserver();
    }
}, 250);

// Listen for URL changes
window.addEventListener("popstate", handleUrlChange);

// jQuery initialization with improved color selection handler
$(function () {
    const handleVariantChange = debounce(function ($changedInput) {
        const $variantWrapper = $changedInput.closest(".variant-wrapper");

        // Get ALL currently selected options from ALL variant input wraps
        const selectedOptions = [];

        // Find all variant input wraps in the page-content (not just variant-wrapper)
        const $pageContent = $changedInput.closest(".page-content");
        $pageContent.find(".variant-input-wrap input:checked").each(function () {
            const value = $(this).val();
            if (value && !selectedOptions.includes(value)) {
                selectedOptions.push(value);
            }
        });

        console.log("Variant change detected, selected options:", selectedOptions);

        waitForVariantData()
            .then((variantData) => {
                // Check if image display should be skipped
                if (shouldSkipImageDisplay(variantData)) {
                    console.log("Skipping image display due to shipping accessories tag");
                    return; // Exit early - no image should be shown
                }

                console.log("Product tags:", variantData.tags);
                console.log(
                    "Available variants:",
                    variantData.variants.map((v) => ({
                        id: v.id,
                        option1: v.option1,
                        option2: v.option2,
                        option3: v.option3,
                        featured_image: v.featured_image,
                    }))
                );

                // Find variant that matches ALL selected options
                const variant = variantData.variants.find((v) => {
                    const variantOptions = [v.option1, v.option2, v.option3].filter(Boolean);
                    console.log("Checking variant:", { variantOptions, selectedOptions });

                    // Check if all selected options are in this variant AND same length
                    return selectedOptions.length === variantOptions.length &&
                        selectedOptions.every(opt => variantOptions.includes(opt));
                });

                console.log("Found variant for options", selectedOptions, ":", variant);

                if (variant) {
                    const $productViewer = $changedInput
                        .closest(".page-content")
                        .find(".product_viewer--iframe");

                    console.log("Product viewer found:", $productViewer.length, $productViewer[0]);

                    if ($productViewer.length) {
                        console.log("Updating product image for variant:", variant.id);
                        console.log("Variant featured_image:", variant.featured_image);

                        // Force update by clearing any existing image first
                        const existingImg = $productViewer[0].querySelector("img");
                        if (existingImg) {
                            console.log("Removing existing image");
                            existingImg.remove();
                        }

                        updateProductImage(variant.id, $productViewer[0]);
                        $productViewer[0].style.paddingBottom = `82.6% !important`;
                        const newUrl = new URL(window.location);
                        newUrl.searchParams.set("variant", variant.id);
                        window.history.replaceState({}, "", newUrl.toString());
                    } else {
                        console.log("Product viewer not found");
                    }
                } else {
                    console.log("No variant found for options:", selectedOptions);
                }
            })
            .catch((error) => {
                console.error("Error handling variant selection:", error);
            });
    }, 250);

    // Listen for ALL variant changes, not just color
    $("html").on(
        "change",
        '.variant-input-wrap input',
        function () {
            handleVariantChange($(this));
        }
    );

    // Initialize default image on page load
    initializeObserver();


    // Remove the separate click handlers for color swatches since we're handling everything in the change event
    // If you had any custom swatch click functionality that needs to be preserved, you can add it to the change handler above
    //Pause Video when out of view
    let video = document.querySelector("video");
    let isPaused = false; /* Flag for auto-paused video */
    if (video) {
        let videoObserver = new IntersectionObserver(
            (entries, observer) => {
                entries.forEach((entry) => {
                    //       console.log(entry.intersectionRatio)
                    if (entry.intersectionRatio != 1 && !video.paused) {
                        video.pause();
                        isPaused = true;
                    } else if (isPaused) {
                        video.play();
                        isPaused = false;
                    }
                });
            },
            { threshold: 1 }
        );
        videoObserver.observe(video);
    }

    const $socials = $(".social-section__grid");
    if ($socials.length > 0) {
        const settings = {
            url: "https://instafeed-api.web.app/api/v1/vallkree/",
            method: "GET",
            timeout: 0,
        };
        //   $socials.find(".image-wrap").html("");
        const $social = $(".grid__item.grid-product--medium").first();
        $.ajax(settings)
            .done(function (response) {
                console.log(response);
                for (let i = 0; i < response.length; i++) {
                    // console.log(response[i])
                    const $clone = $social.clone();
                    $clone.appendTo(".social-section__grid");
                    $clone.find(".social-section__caption").text(response[i].text);
                    //        $clone.find('.social-section__image').attr('bg-set','').css('background-image','none !important;');
                    var $imgContainer = $('<div class="social-section__image" />'),
                        $img = $("<img />");

                    //{'background-image': 'url("'+ response[i].src+'")'      }
                    $imgContainer.attr("title", response[i].alt).append($img);
                    $img
                        .attr(
                            "src",
                            `https://instafeed-api.web.app/api/v1/img/${btoa(
                                response[i].src
                            )}`
                        )
                        .on("error", function () {
                            //        alert('error')
                            $(this).closest(".grid__item").remove();
                        });

                    $clone.find(".social-section__image").remove();
                    $imgContainer.prependTo(
                        $clone.find("a").attr("href", response[i].url)
                    ); //.html('<div style="background-image:url(\'`${response[i].src}`\')"')

                    //url("'+response[i].src+'")

                    //         .find(".image-wrap")
                    //         .append(
                    //           '<a href="' +
                    //             response[i].href +
                    //             '" target="_blank"><img src="' +
                    //             response[i].src +
                    //             '" alt="' +
                    //             response[i].alt +
                    //             '" /></a>'
                    //         );
                }
            })
            .always(function () {
                $social.remove();
            });
    }
}); //End/jQuery

// Custom Discount System
// Handles discount tags like "discount:2000" (fixed amount) or "discount:20%" (percentage)
const CustomDiscountSystem = {
    // Parse discount from product tags
    parseDiscountFromTags: function (tags) {
        if (!tags || !Array.isArray(tags)) return null;

        for (let tag of tags) {
            if (tag.includes('discount:')) {
                const discountValue = tag.replace('discount:', '');

                if (discountValue.includes('%')) {
                    return {
                        type: 'percentage',
                        value: parseFloat(discountValue.replace('%', ''))
                    };
                } else {
                    return {
                        type: 'fixed',
                        value: parseFloat(discountValue) * 100 // Convert to cents
                    };
                }
            }
        }
        return null;
    },

    // Calculate sale price based on discount
    calculateSalePrice: function (originalPrice, discount) {
        if (!discount) return originalPrice;

        let salePrice;
        if (discount.type === 'percentage') {
            const discountAmount = originalPrice * (discount.value / 100);
            salePrice = originalPrice - discountAmount;
        } else {
            salePrice = originalPrice - discount.value;
        }

        return Math.max(0, salePrice); // Ensure price doesn't go below 0
    },

    // Format price for display
    formatPrice: function (priceInCents, currencySymbol = '$') {
        return currencySymbol + (priceInCents / 100).toFixed(2);
    },

    // Update price display on the page
    updatePriceDisplay: function (originalPrice, salePrice, discount) {
        const priceContainer = document.querySelector('.product-single__prices');
        if (!priceContainer) return;

        // Clear existing price content
        priceContainer.innerHTML = '';

        if (discount && salePrice < originalPrice) {
            // Show discount pricing
            const comparePrice = document.createElement('span');
            comparePrice.className = 'product__price product__price--compare';
            comparePrice.setAttribute('data-product-price-compare', '');
            comparePrice.textContent = this.formatPrice(originalPrice);

            const currentPrice = document.createElement('span');
            currentPrice.className = 'h3 product-single__title product__price sale-price';
            currentPrice.setAttribute('data-product-price', '');
            currentPrice.textContent = this.formatPrice(salePrice);

            const discountBadge = document.createElement('span');
            discountBadge.className = 'product__discount-badge';
            if (discount.type === 'percentage') {
                discountBadge.textContent = `Save ${discount.value}%`;
            } else {
                discountBadge.textContent = `Save ${this.formatPrice(discount.value)}`;
            }

            // Add accessibility labels
            const regularPriceLabel = document.createElement('span');
            regularPriceLabel.className = 'visually-hidden';
            regularPriceLabel.setAttribute('data-price-a11y', '');
            regularPriceLabel.textContent = 'Regular price';

            const salePriceLabel = document.createElement('span');
            salePriceLabel.className = 'visually-hidden';
            salePriceLabel.setAttribute('data-compare-a11y', '');
            salePriceLabel.textContent = 'Sale price';

            priceContainer.appendChild(regularPriceLabel);
            priceContainer.appendChild(comparePrice);
            priceContainer.appendChild(salePriceLabel);
            priceContainer.appendChild(currentPrice);
            priceContainer.appendChild(discountBadge);
        } else {
            // Show regular pricing
            const regularPriceLabel = document.createElement('span');
            regularPriceLabel.className = 'visually-hidden';
            regularPriceLabel.setAttribute('data-price-a11y', '');
            regularPriceLabel.textContent = 'Regular price';

            const currentPrice = document.createElement('span');
            currentPrice.className = 'h3 product-single__title product__price';
            currentPrice.setAttribute('data-product-price', '');
            currentPrice.textContent = this.formatPrice(originalPrice);

            priceContainer.appendChild(regularPriceLabel);
            priceContainer.appendChild(currentPrice);
        }
    },

    // Initialize discount system
    init: function () {
        // Wait for variant data to be available
        waitForVariantData().then((productData) => {
            const discount = this.parseDiscountFromTags(productData.tags);

            if (discount) {
                console.log('Custom discount found:', discount);

                // Update pricing for current variant
                this.updateVariantPricing(productData, discount);

                // Listen for variant changes
                this.setupVariantChangeListener(productData, discount);
            }
        }).catch((error) => {
            console.error('Error initializing custom discount system:', error);
        });
    },

    // Update pricing for current variant
    updateVariantPricing: function (productData, discount) {
        const currentVariantId = new URLSearchParams(window.location.search).get('variant');
        let currentVariant;

        if (currentVariantId) {
            currentVariant = productData.variants.find(v => v.id.toString() === currentVariantId);
        } else {
            currentVariant = productData.variants[0]; // First available variant
        }

        if (currentVariant) {
            const originalPrice = currentVariant.price;
            const salePrice = this.calculateSalePrice(originalPrice, discount);
            this.updatePriceDisplay(originalPrice, salePrice, discount);
        }
    },

    // Setup listener for variant changes
    setupVariantChangeListener: function (productData, discount) {
        // Listen for variant selection changes
        $(document).on('change', '.variant-input-wrap input, select[data-product-select]', () => {
            setTimeout(() => {
                this.updateVariantPricing(productData, discount);
            }, 100); // Small delay to ensure variant data is updated
        });

        // Listen for URL changes (back/forward navigation)
        window.addEventListener('popstate', () => {
            setTimeout(() => {
                this.updateVariantPricing(productData, discount);
            }, 100);
        });
    }
};

// Initialize the custom discount system when the page loads
$(document).ready(function () {
    // Temporarily disabled to debug Liquid template
    // CustomDiscountSystem.init();
    console.log('Custom discount system temporarily disabled for debugging');
});